// src/components/Navbar.js
//
// Top navigation bar
// ==================
//
// PURPOSE
// -------
// This component renders the authenticated application top bar.
//
// Current responsibilities:
// - adapt visually to expanded / collapsed sidebar width,
// - show a greeting / current user identity,
// - show announcements when active,
// - keep Command Search visible even while an announcement is active,
// - render runtime announcements as a separate overlay so search position is stable,
// - render a Command Search entry button,
// - show user menu on the far right,
// - move account actions into the user dropdown:
//     - Profile Language (durable backend profile setting)
//     - Change Password
//     - Logout
//
// IMPORTANT ARCHITECTURAL UPDATE
// ------------------------------
// Command Search is no longer implemented inside Navbar.
//
// Navbar now imports and renders only:
//
//   <CommandSearchButton />
//
// The dialog, keyboard shortcut, result filtering and future backend search are
// owned by:
//
//   src/components/commandSearch/*
//
// This keeps Navbar focused on top-bar layout and account actions.
//
// CURRENT APP ARCHITECTURE
// ------------------------
// 1) AuthContext owns authentication state and explicit logout behavior.
// 2) apiClient owns refresh/session recovery behavior.
// 3) Navbar must NOT make hard auth decisions except explicit user-triggered logout.
//
// Therefore:
// - fetch failures for user details / announcements are treated as local UI issues,
// - Navbar does NOT call softLogout(),
// - Navbar does NOT attempt auth recovery.
//
// IMPORTANT CSS / INTEGRATION RULE
// --------------------------------
// The following class names must remain present because another part of the app
// may rely on them for navigation blocking / click interception:
//
// - .change-password-button
// - .logout-button
//
// They are rendered inside the user dropdown, but the classes remain.
//
// LANGUAGE ARCHITECTURE NOTE
// --------------------------
// The sidebar LanguageSwitcher controls the CURRENT displayed UI language only.
// The profile-language selector rendered in this dropdown changes the DURABLE
// profile preference in the backend. These two values may intentionally differ
// when a user chose a manual language override during login.

import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import "./Navbar.css";

import AuthContext from "../context/AuthContext";
import {
  fetchUserById,
  getApiErrorMessage as getUserApiErrorMessage,
} from "../services/userService";
import { fetchActiveAnnouncements } from "../services/announcementService";
import { isRequestCanceled } from "../services/httpUtils";
import { getAnnouncementTickerDuration } from "../utils/announcementTicker";
import CommandSearchButton from "./commandSearch/CommandSearchButton";
import ProfileLanguageDropdown from "./ProfileLanguageDropdown";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Timing constants                                                           */
/* -------------------------------------------------------------------------- */

const ANNOUNCEMENTS_REFRESH_MS = 3 * 60 * 1000; // 3 minutes
const ANNOUNCEMENT_ROTATE_MS = 5000; // 5 seconds
const PASSWORD_SUCCESS_CLOSE_MS = 900;

/* -------------------------------------------------------------------------- */
/* Responsive announcement layout constants                                   */
/* -------------------------------------------------------------------------- */

/*
 * The authenticated Navbar has three valid runtime-announcement layouts:
 *
 * 1) overlay
 *    The default desktop layout. The announcement remains a small absolute
 *    overlay near the greeting. This keeps the top bar compact and preserves
 *    the original left / center / right grid.
 *
 * 2) inline
 *    The intermediate desktop-zoom layout. The announcement becomes a normal
 *    grid column between the greeting and Command Search. Command Search moves
 *    slightly to the right instead of being overlapped by the announcement.
 *    This keeps the Navbar in one row, which is much safer for pages that have
 *    bottom pagination or other viewport-sensitive controls.
 *
 * 3) two_row
 *    The compact two-row zoom layout. The announcement still remains in the
 *    first row between the greeting and the right controls, while Command
 *    Search moves to a full-width second row. This gives the Navbar one extra
 *    row only, instead of immediately pushing the announcement into a third
 *    row.
 *
 * 4) stacked
 *    The last-resort constrained layout. If even the first row cannot safely
 *    hold the greeting, announcement and right controls, Command Search keeps
 *    its own row and the announcement moves into a final full-width row.
 *
 * Why this is JS-driven instead of a pure viewport breakpoint:
 * A viewport breakpoint cannot see the real available Navbar width after the
 * sidebar width, browser zoom, translated texts, user name length and collapsed
 * sidebar state are taken into account. ResizeObserver lets us react to the
 * actual rendered component width instead of guessing with values such as
 * 1320px or 1700px.
 */
const NAVBAR_DESKTOP_ADAPTIVE_MIN_WIDTH_PX = 992;

/*
 * Layout modes are intentionally stored as strings because they are rendered as
 * CSS classes. Keeping the names explicit makes the CSS easier to read and
 * makes debugging in browser dev tools much clearer.
 */
const NAVBAR_ANNOUNCEMENT_LAYOUT = Object.freeze({
  OVERLAY: "overlay",
  INLINE: "inline",
  TWO_ROW: "two_row",
  STACKED: "stacked",
});

/*
 * Conservative buffers used by the measured demand calculation.
 *
 * The overlay layout needs the largest breathing room because the announcement
 * is visually absolute and therefore cannot participate in grid collision
 * prevention. Inline layout needs less buffer because the announcement receives
 * its own grid column and Command Search naturally moves to the right. Two-row
 * layout needs a first-row buffer only for greeting + announcement + right-side
 * controls because Command Search has already moved into its own row.
 */
const NAVBAR_OVERLAY_SAFETY_BUFFER_PX = 96;
const NAVBAR_INLINE_SAFETY_BUFFER_PX = 48;
const NAVBAR_TWO_ROW_SAFETY_BUFFER_PX = 36;
const NAVBAR_CENTER_ROW_SAFETY_BUFFER_PX = 40;

/*
 * Overlay collision guard.
 *
 * Width-only checks are not enough for the default desktop overlay mode. The
 * total widgets may technically fit into the Navbar, while the absolutely
 * positioned announcement still overlaps the centered Command Search button.
 * This gap is the visual safety distance required between the right edge of
 * the overlay announcement and the left edge of the Search button. When the
 * gap cannot be kept, Navbar should switch from overlay to inline mode so
 * Search can move right as a real grid item instead of being covered.
 */
const NAVBAR_OVERLAY_COLLISION_GAP_PX = 28;

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

function formatUserDisplay(user, fallbackUserLabel = "User") {
  if (!user) {
    return {
      username: fallbackUserLabel,
      fullName: "",
      firstName: fallbackUserLabel,
      role: "",
      fullDisplay: fallbackUserLabel,
      initials: "U",
      displayName: fallbackUserLabel,
    };
  }

  const username = String(user.username || "").trim() || fallbackUserLabel;
  const firstName = String(user.first_name || "").trim();
  const lastName = String(user.last_name || "").trim();
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const role = String(user.role || "").trim();

  const displayName = fullName || username;
  const initialsSource = fullName || username;
  const initials =
    initialsSource
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "U";

  return {
    username,
    fullName,
    firstName: firstName || username,
    role,
    fullDisplay: fullName ? `${username} (${fullName})` : username,
    initials,
    displayName,
  };
}

function getEmptyPasswordFormState() {
  return {
    currentPassword: "",
    newPassword: "",
    errorMessage: "",
    successMessage: "",
  };
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getElementWidth(element) {
  if (!element || typeof element.getBoundingClientRect !== "function") {
    return 0;
  }

  return element.getBoundingClientRect().width || 0;
}

function getHorizontalPadding(element) {
  if (!element || typeof window === "undefined") {
    return 0;
  }

  const styles = window.getComputedStyle(element);
  const paddingLeft = Number.parseFloat(styles.paddingLeft || "0") || 0;
  const paddingRight = Number.parseFloat(styles.paddingRight || "0") || 0;

  return paddingLeft + paddingRight;
}

function getFlexChildrenWidth(element) {
  if (!element || typeof window === "undefined") {
    return 0;
  }

  const children = Array.from(element.children || []);

  if (children.length === 0) {
    return getElementWidth(element);
  }

  const styles = window.getComputedStyle(element);
  const gap = Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;

  const childrenWidth = children.reduce((sum, child) => {
    return sum + getElementWidth(child);
  }, 0);

  return childrenWidth + Math.max(0, children.length - 1) * gap;
}

function getCenterControlWidth(centerElement) {
  if (!centerElement) {
    return 0;
  }

  /*
   * The center grid column may be wider than the actual CommandSearchButton,
   * especially after the Navbar switches to inline or stacked mode. For layout
   * decisions we care about the control that must stay visible and clickable,
   * not about the temporary grid column width.
   */
  const firstChild = centerElement.firstElementChild;
  const measuredChildWidth = getElementWidth(firstChild);

  /*
   * Keep the original Navbar grid minimum as a lower bound. This prevents the
   * calculation from becoming too optimistic before the child is fully measured
   * during the first render.
   */
  return Math.max(260, measuredChildWidth);
}

function getPreferredOverlayAnnouncementWidth(navbarWidth) {
  /*
   * Mirrors the intent of the CSS clamp-based announcement width profile for
   * the default overlay mode.
   *
   * We intentionally do not read CSS custom properties here because custom
   * property values containing clamp() are not reliably returned as resolved
   * pixels by getComputedStyle. A small JS mirror is more predictable and keeps
   * the decision based on the real measured Navbar width.
   */
  if (navbarWidth <= 1100) {
    return clampNumber(navbarWidth * 0.26, 240, 300);
  }

  if (navbarWidth <= 1280) {
    return clampNumber(navbarWidth * 0.28, 280, 470);
  }

  return clampNumber(navbarWidth * 0.28, 300, 560);
}


function getPreferredOverlayAnnouncementLeft() {
  /*
   * Mirrors the CSS value of --navbar-announcement-left.
   *
   * The CSS uses viewport units inside clamp(), so the calculation must use the
   * current CSS viewport width, not the Navbar width. Keeping this mirror in JS
   * lets the layout decision predict whether the default absolute overlay would
   * collide with Command Search before actually switching modes.
   */
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;

  if (viewportWidth <= 1100) {
    return 270;
  }

  if (viewportWidth <= 1280) {
    return clampNumber(viewportWidth * 0.23, 285, 360);
  }

  return clampNumber(viewportWidth * 0.24, 310, 430);
}

function getDefaultGridMinimums() {
  /*
   * Mirrors the default desktop grid minimums from Navbar.css.
   *
   * At <= 1220px the CSS slightly reduces the left and center minimums. The
   * measured collision guard needs to know these values because the position of
   * the centered Command Search button depends on the default grid distribution
   * in overlay mode.
   */
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;

  if (viewportWidth <= 1220) {
    return {
      leftMin: 200,
      centerMin: 220,
    };
  }

  return {
    leftMin: 230,
    centerMin: 260,
  };
}

function getGridColumnGap(element) {
  if (!element || typeof window === "undefined") {
    return 14;
  }

  const styles = window.getComputedStyle(element);
  const columnGap =
    Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;

  return columnGap || 14;
}

function canOverlayAnnouncementStayClearOfSearch({
  navbarElement,
  navbarWidth,
  usableWidth,
  rightWidth,
  centerControlWidth,
  overlayAnnouncementWidth,
}) {
  /*
   * The default overlay mode is visually special: the announcement is absolute,
   * while Search is centered by the grid. A simple total-width calculation can
   * still say "everything fits" even when the overlay visually touches Search.
   *
   * This function predicts the default grid geometry and checks the actual
   * visual relationship we care about:
   *
   *   overlay right edge + safety gap <= Search button left edge
   *
   * If this condition fails, we do not wait for a hard overlap. We switch to the
   * inline layout, where the announcement receives a real grid column and Search
   * can move right in a controlled way.
   */
  if (!navbarElement || usableWidth <= 0) {
    return true;
  }

  const { leftMin, centerMin } = getDefaultGridMinimums();
  const columnGap = getGridColumnGap(navbarElement);

  const availableForFlexibleColumns = Math.max(
    0,
    usableWidth - rightWidth - columnGap * 2
  );

  const minimumColumnsWidth = leftMin + centerMin;
  const extraWidth = Math.max(0, availableForFlexibleColumns - minimumColumnsWidth);

  const leftColumnWidth = leftMin + extraWidth * (0.85 / 1.85);
  const centerColumnWidth = centerMin + extraWidth * (1 / 1.85);

  const centerControlLeft =
    leftColumnWidth +
    columnGap +
    Math.max(0, (centerColumnWidth - centerControlWidth) / 2);

  const overlayLeft = getPreferredOverlayAnnouncementLeft(navbarWidth);
  const overlayRight = overlayLeft + overlayAnnouncementWidth;

  return overlayRight + NAVBAR_OVERLAY_COLLISION_GAP_PX <= centerControlLeft;
}

function getPreferredInlineAnnouncementWidth(navbarWidth) {
  /*
   * Inline mode is the intermediate, one-row fallback. It should be readable,
   * but it may be narrower than the desktop overlay because the announcement is
   * now a real grid item and Command Search must remain visible in the same row.
   */
  return clampNumber(navbarWidth * 0.22, 220, 380);
}

function getPreferredTwoRowAnnouncementWidth(navbarWidth) {
  /*
   * Compact two-row mode has more horizontal room for the announcement because
   * Command Search no longer competes for first-row space. We can therefore let
   * the message breathe more than in inline mode, while still keeping enough
   * room for the right-side user controls.
   */
  return clampNumber(navbarWidth * 0.32, 240, 560);
}

function getNavbarAnnouncementLayoutMode({
  navbarElement,
  leftElement,
  centerElement,
  rightElement,
}) {
  if (!navbarElement || typeof window === "undefined") {
    return NAVBAR_ANNOUNCEMENT_LAYOUT.OVERLAY;
  }

  /*
   * Below the desktop threshold the CSS mobile/tablet layout already stacks the
   * Navbar. Returning overlay here avoids adding a JS-driven desktop mode on top
   * of the mobile rules.
   */
  if (window.innerWidth < NAVBAR_DESKTOP_ADAPTIVE_MIN_WIDTH_PX) {
    return NAVBAR_ANNOUNCEMENT_LAYOUT.OVERLAY;
  }

  const navbarWidth = getElementWidth(navbarElement);
  const usableWidth = Math.max(
    0,
    navbarWidth - getHorizontalPadding(navbarElement)
  );

  /*
   * Do not use the grid cell widths directly here. Once Navbar has switched to
   * inline or stacked mode, the grid cells may become much wider than their real
   * content. Measuring the actual flex children keeps the decision reversible:
   * when the user decreases browser zoom or collapses the sidebar, Navbar can
   * correctly return from stacked -> inline -> overlay.
   */
  const leftWidth = getFlexChildrenWidth(leftElement);
  const rightWidth = getFlexChildrenWidth(rightElement);
  const centerControlWidth = getCenterControlWidth(centerElement);

  const overlayAnnouncementWidth = getPreferredOverlayAnnouncementWidth(navbarWidth);
  const inlineAnnouncementWidth = getPreferredInlineAnnouncementWidth(navbarWidth);
  const twoRowAnnouncementWidth = getPreferredTwoRowAnnouncementWidth(navbarWidth);

  const overlayDemand =
    leftWidth +
    overlayAnnouncementWidth +
    centerControlWidth +
    rightWidth +
    NAVBAR_OVERLAY_SAFETY_BUFFER_PX;

  const overlayHasEnoughTotalWidth = overlayDemand <= usableWidth;
  const overlayStaysClearOfSearch = canOverlayAnnouncementStayClearOfSearch({
    navbarElement,
    navbarWidth,
    usableWidth,
    rightWidth,
    centerControlWidth,
    overlayAnnouncementWidth,
  });

  if (overlayHasEnoughTotalWidth && overlayStaysClearOfSearch) {
    return NAVBAR_ANNOUNCEMENT_LAYOUT.OVERLAY;
  }

  /*
   * Inline demand is intentionally smaller than overlay demand. Before giving
   * up and increasing Navbar height, we first let Command Search move right in
   * a controlled four-column grid. This is the preferred zoom/medium-width
   * fallback because it still keeps all important widgets in one row.
   */
  const inlineDemand =
    leftWidth +
    inlineAnnouncementWidth +
    centerControlWidth +
    rightWidth +
    NAVBAR_INLINE_SAFETY_BUFFER_PX;

  if (inlineDemand <= usableWidth) {
    return NAVBAR_ANNOUNCEMENT_LAYOUT.INLINE;
  }

  /*
   * Compact two-row demand is the next fallback. At this point Search Command
   * moves to a full-width second row, but the announcement remains in the first
   * row together with greeting and right-side controls. This gives us additional
   * pixels before using the more expensive three-row stacked layout.
   */
  const firstRowDemand =
    leftWidth +
    twoRowAnnouncementWidth +
    rightWidth +
    NAVBAR_TWO_ROW_SAFETY_BUFFER_PX;

  const centerRowDemand =
    centerControlWidth + NAVBAR_CENTER_ROW_SAFETY_BUFFER_PX;

  if (firstRowDemand <= usableWidth && centerRowDemand <= usableWidth) {
    return NAVBAR_ANNOUNCEMENT_LAYOUT.TWO_ROW;
  }

  return NAVBAR_ANNOUNCEMENT_LAYOUT.STACKED;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const Navbar = () => {
  const { authState, handleLogout, changePassword } = useContext(AuthContext);
  const { t, i18n } = useTranslation(["common"]);
  const location = useLocation();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [isLogoutSubmitting, setIsLogoutSubmitting] = useState(false);

  const [passwordForm, setPasswordForm] = useState(getEmptyPasswordFormState());

  const [currentUser, setCurrentUser] = useState(() => authState?.user || null);
  const [isUserLoading, setIsUserLoading] = useState(false);

  const [announcements, setAnnouncements] = useState([]);
  const [announcementIndex, setAnnouncementIndex] = useState(0);

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const passwordCloseTimerRef = useRef(null);
  const userAbortRef = useRef(null);
  const isMountedRef = useRef(false);
  const userMenuRef = useRef(null);

  const navbarRef = useRef(null);
  const navbarLeftRef = useRef(null);
  const navbarCenterRef = useRef(null);
  const navbarRightRef = useRef(null);

  const [announcementLayoutMode, setAnnouncementLayoutMode] = useState(
    NAVBAR_ANNOUNCEMENT_LAYOUT.OVERLAY
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (passwordCloseTimerRef.current) {
        clearTimeout(passwordCloseTimerRef.current);
        passwordCloseTimerRef.current = null;
      }

      if (userAbortRef.current) {
        userAbortRef.current.abort();
        userAbortRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setCurrentUser(authState?.user || null);
  }, [authState?.user]);

  useEffect(() => {
    const userId = authState?.user?.id;

    if (!authState?.isAuthenticated || !userId) {
      setCurrentUser(null);
      return undefined;
    }

    if (userAbortRef.current) {
      userAbortRef.current.abort();
    }

    const controller = new AbortController();
    userAbortRef.current = controller;

    const run = async () => {
      setIsUserLoading(true);

      try {
        const user = await fetchUserById(userId, {
          signal: controller.signal,
        });

        if (!isMountedRef.current || controller.signal.aborted) return;

        setCurrentUser(user || authState.user || null);
      } catch (error) {
        if (isRequestCanceled(error) || controller.signal.aborted) return;

        if (DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[Navbar] failed to load user ${userId}:`, error);
        }

        if (!isMountedRef.current) return;
        setCurrentUser(authState.user || null);
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setIsUserLoading(false);
        }
      }
    };

    run();

    return () => {
      controller.abort();
    };
  }, [authState?.isAuthenticated, authState?.user?.id, authState?.user]);

  useEffect(() => {
    if (isPasswordModalOpen) {
      return undefined;
    }

    let cancelled = false;

    const loadAnnouncements = async () => {
      try {
        const response = await fetchActiveAnnouncements({
          audience: "internal",
          surface: "internal_navbar",
          path: location.pathname,
          lang: i18n.resolvedLanguage || i18n.language || "pl",
        });

        if (cancelled || !isMountedRef.current) return;

        const nextAnnouncements = Array.isArray(response) ? response : [];
        setAnnouncements(nextAnnouncements);
        setAnnouncementIndex(0);
      } catch (error) {
        if (DEV) {
          // eslint-disable-next-line no-console
          console.warn("[Navbar] failed to load announcements:", error);
        }

        if (!cancelled && isMountedRef.current) {
          setAnnouncements([]);
          setAnnouncementIndex(0);
        }
      }
    };

    loadAnnouncements();

    const intervalId = window.setInterval(
      loadAnnouncements,
      ANNOUNCEMENTS_REFRESH_MS
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isPasswordModalOpen, location.pathname, i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    if (isPasswordModalOpen) return undefined;
    if (announcements.length <= 1) return undefined;

    const intervalId = window.setInterval(() => {
      setAnnouncementIndex((prev) => (prev + 1) % announcements.length);
    }, ANNOUNCEMENT_ROTATE_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [announcements, isPasswordModalOpen]);

  const userDisplay = useMemo(
    () => formatUserDisplay(currentUser, t("common:navbar.userFallback")),
    [currentUser, t]
  );

  const userRoleLabel = useMemo(() => {
    const rawRole = String(userDisplay.role || "").trim();

    if (!rawRole) {
      return isUserLoading
        ? t("common:common.loading")
        : t("common:navbar.authenticated");
    }

    return t(`common:roles.${rawRole.toLowerCase()}`, { defaultValue: rawRole });
  }, [isUserLoading, t, userDisplay.role]);

  const greetingText = useMemo(() => {
    return t("common:navbar.greeting", { name: userDisplay.firstName });
  }, [t, userDisplay.firstName]);

  const currentAnnouncement = announcements[announcementIndex] || null;

  const currentAnnouncementText = useMemo(() => {
    return String(currentAnnouncement?.message || "").trim();
  }, [currentAnnouncement]);

  const currentAnnouncementDuration = useMemo(() => {
    if (!currentAnnouncementText || !currentAnnouncement) return undefined;

    /*
     * The duration is derived from the runtime announcement definition.
     *
     * Current announcement architecture supports ticker_speed values such as
     * "very_slow", "slow", "normal", "slightly_fast", "fast",
     * "very_fast" and "maximum".
     * The calculation itself is intentionally kept in a shared utility so the
     * internal Navbar and the public shell banner follow the same speed model
     * without coupling their CSS layouts.
     */
    return getAnnouncementTickerDuration(currentAnnouncement);
  }, [currentAnnouncement, currentAnnouncementText]);

  /**
   * Runtime announcement visibility.
   *
   * Important UX rule:
   * The Command Search button must remain mounted and keep its normal position
   * even when a runtime announcement is active. Earlier implementations used
   * the same center slot for either announcement OR search. That made the
   * search entry disappear during active announcements and also changed the
   * visual balance of the navbar.
   *
   * Therefore the announcement is rendered as a separate navbar child. In the
   * default desktop mode it is positioned by CSS as an overlay. When measured
   * horizontal room becomes tight, it can move into an inline grid column, then
   * into a compact two-row layout where Search gets its own row, and only as a
   * final fallback into the full stacked layout. Command Search remains mounted
   * in all modes.
   */
  const shouldShowNavbarAnnouncement =
    !isPasswordModalOpen && Boolean(currentAnnouncementText);

  useLayoutEffect(() => {
    if (!shouldShowNavbarAnnouncement) {
      setAnnouncementLayoutMode(NAVBAR_ANNOUNCEMENT_LAYOUT.OVERLAY);
      return undefined;
    }

    let animationFrameId = null;

    const evaluateLayout = () => {
      animationFrameId = null;

      const nextLayoutMode = getNavbarAnnouncementLayoutMode({
        navbarElement: navbarRef.current,
        leftElement: navbarLeftRef.current,
        centerElement: navbarCenterRef.current,
        rightElement: navbarRightRef.current,
      });

      setAnnouncementLayoutMode((previousValue) => {
        return previousValue === nextLayoutMode ? previousValue : nextLayoutMode;
      });
    };

    const scheduleEvaluation = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(evaluateLayout);
    };

    scheduleEvaluation();

    const observedElements = [
      navbarRef.current,
      navbarLeftRef.current,
      navbarCenterRef.current,
      navbarRightRef.current,
    ].filter(Boolean);

    const resizeObserver =
      typeof window.ResizeObserver === "function"
        ? new window.ResizeObserver(scheduleEvaluation)
        : null;

    if (resizeObserver) {
      observedElements.forEach((element) => resizeObserver.observe(element));
    } else {
      window.addEventListener("resize", scheduleEvaluation);
    }

    /*
     * Browser zoom normally triggers a resize event, but some browsers expose a
     * more precise visualViewport resize event. Listening to both makes the
     * overlay -> inline -> stacked transitions more reliable during manual zoom
     * testing.
     */
    const visualViewport = window.visualViewport || null;

    if (visualViewport) {
      visualViewport.addEventListener("resize", scheduleEvaluation);
    }

    /*
     * Sidebar state in this app is represented by body classes. In most cases
     * ResizeObserver catches the resulting Navbar width change. The mutation
     * observer below is an additional safety net for browsers that batch layout
     * updates aggressively during class changes.
     */
    const bodyClassObserver =
      typeof window.MutationObserver === "function"
        ? new window.MutationObserver(scheduleEvaluation)
        : null;

    if (bodyClassObserver) {
      bodyClassObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    window.addEventListener("orientationchange", scheduleEvaluation);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", scheduleEvaluation);
      }

      if (visualViewport) {
        visualViewport.removeEventListener("resize", scheduleEvaluation);
      }

      if (bodyClassObserver) {
        bodyClassObserver.disconnect();
      }

      window.removeEventListener("orientationchange", scheduleEvaluation);
    };
  }, [
    shouldShowNavbarAnnouncement,
    currentAnnouncementText,
    greetingText,
    userDisplay.displayName,
    userRoleLabel,
  ]);

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const onPointerDown = (event) => {
      if (!userMenuRef.current) return;
      if (userMenuRef.current.contains(event.target)) return;
      setIsUserMenuOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsUserMenuOpen(false);
      }
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isUserMenuOpen]);

  const openPasswordModal = () => {
    setIsUserMenuOpen(false);
    setPasswordForm(getEmptyPasswordFormState());
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    if (isPasswordSubmitting) return;

    if (passwordCloseTimerRef.current) {
      clearTimeout(passwordCloseTimerRef.current);
      passwordCloseTimerRef.current = null;
    }

    setIsPasswordModalOpen(false);
    setPasswordForm(getEmptyPasswordFormState());
  };

  const updatePasswordField = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePassword = async () => {
    const currentPassword = String(passwordForm.currentPassword || "");
    const newPassword = String(passwordForm.newPassword || "");

    setPasswordForm((prev) => ({
      ...prev,
      errorMessage: "",
      successMessage: "",
    }));

    if (!currentPassword || !newPassword) {
      setPasswordForm((prev) => ({
        ...prev,
        errorMessage: t("common:navbar.password.errors.required"),
      }));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordForm((prev) => ({
        ...prev,
        errorMessage: t("common:navbar.password.errors.minLength", { min: 8 }),
      }));
      return;
    }

    setIsPasswordSubmitting(true);

    try {
      await changePassword(currentPassword, newPassword);

      if (!isMountedRef.current) return;

      setPasswordForm((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        errorMessage: "",
        successMessage: t("common:navbar.password.success"),
      }));

      passwordCloseTimerRef.current = window.setTimeout(() => {
        passwordCloseTimerRef.current = null;
        if (!isMountedRef.current) return;
        closePasswordModal();
      }, PASSWORD_SUCCESS_CLOSE_MS);
    } catch (error) {
      if (!isMountedRef.current) return;

      if (DEV) {
        // eslint-disable-next-line no-console
        console.warn("[Navbar] password change failed:", error);
      }

      setPasswordForm((prev) => ({
        ...prev,
        errorMessage: getUserApiErrorMessage(
          error,
          t("common:navbar.password.errors.failed")
        ),
        successMessage: "",
      }));
    } finally {
      if (isMountedRef.current) {
        setIsPasswordSubmitting(false);
      }
    }
  };

  const onLogoutClick = async () => {
    if (isLogoutSubmitting) return;

    setIsUserMenuOpen(false);
    setIsLogoutSubmitting(true);

    try {
      await handleLogout();
    } finally {
      if (isMountedRef.current) {
        setIsLogoutSubmitting(false);
      }
    }
  };

  const navbarClassName = [
    "navbar",
    shouldShowNavbarAnnouncement ? "navbar--has-announcement" : null,
    shouldShowNavbarAnnouncement &&
    announcementLayoutMode === NAVBAR_ANNOUNCEMENT_LAYOUT.INLINE
      ? "navbar--announcement-inline"
      : null,
    shouldShowNavbarAnnouncement &&
    announcementLayoutMode === NAVBAR_ANNOUNCEMENT_LAYOUT.TWO_ROW
      ? "navbar--announcement-two-row"
      : null,
    shouldShowNavbarAnnouncement &&
    announcementLayoutMode === NAVBAR_ANNOUNCEMENT_LAYOUT.STACKED
      ? "navbar--announcement-stacked"
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={navbarRef}
      className={navbarClassName}
      role="navigation"
      aria-label={t("common:navbar.topNavigationAriaLabel")}
    >
      <div className="navbar__left" ref={navbarLeftRef}>
        <button
          className="navbar__hamburger"
          type="button"
          aria-label={t("common:navbar.openMenu")}
          onClick={() => document.body.classList.add("sidebar-open")}
        >
          <span aria-hidden="true">☰</span>
        </button>

        <div className="navbar__greeting" title={userDisplay.fullDisplay}>
          <p className="navbar__greetingTitle">{greetingText}</p>
          <p className="navbar__greetingSubtitle">
            {t("common:navbar.subtitle")}
          </p>
        </div>
      </div>

      {shouldShowNavbarAnnouncement ? (
        <div
          className="navbar__announcement"
          aria-live="polite"
          aria-atomic="true"
        >
          {/*
            Internal announcement marquee.

            The outer .navbar__announcement box stays exactly where the stable
            internal Navbar expects it: as an absolute, non-interactive overlay
            near the greeting area. Only the nested viewport/track pair owns the
            animation geometry. This is intentionally different from changing
            the navbar grid or the outer announcement box, because those earlier
            experiments destabilized the sidebar offset, Command Search position
            and right-side user controls.
          */}
          <div className="navbar__announcementViewport">
            <p
              className="navbar__announcementTrack"
              style={
                currentAnnouncementDuration
                  ? { "--navbar-announcement-duration": currentAnnouncementDuration }
                  : undefined
              }
            >
              <span className="navbar__announcementText">
                {currentAnnouncementText}
              </span>
            </p>
          </div>
        </div>
      ) : null}

      <div className="navbar__center" ref={navbarCenterRef}>
        <CommandSearchButton />
      </div>

      <div className="navbar__right" ref={navbarRightRef}>
        <button
          type="button"
          className="navbar__notificationButton"
          aria-label={t("common:navbar.notifications")}
          title={t("common:navbar.notifications")}
        >
          <span className="navbar__notificationIcon" aria-hidden="true">
            🔔
          </span>
          <span className="navbar__notificationBadge">3</span>
        </button>

        <div className="navbar__userMenu" ref={userMenuRef}>
          <button
            type="button"
            className="navbar__userMenuButton"
            onClick={() => setIsUserMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen}
            title={userDisplay.fullDisplay}
          >
            <span className="navbar__avatar" aria-hidden="true">
              {userDisplay.initials}
            </span>

            <span className="navbar__userText">
              <strong>{userDisplay.displayName}</strong>
              <em>{userRoleLabel}</em>
            </span>

            <span className="navbar__chevron" aria-hidden="true">
              ▾
            </span>
          </button>

          {isUserMenuOpen && (
            <div className="navbar__userDropdown" role="menu">
              <div className="navbar__dropdownHeader">
                <strong>{userDisplay.displayName}</strong>
                <span>{userDisplay.username}</span>
              </div>

              <ProfileLanguageDropdown
                disabled={isPasswordSubmitting || isLogoutSubmitting}
              />

              <button
                className="navbar__dropdownItem change-password-button"
                type="button"
                role="menuitem"
                onClick={openPasswordModal}
                disabled={isPasswordSubmitting || isLogoutSubmitting}
              >
                {t("common:navbar.actions.changePassword")}
              </button>

              <button
                className="navbar__dropdownItem navbar__dropdownItem--danger logout-button"
                type="button"
                role="menuitem"
                onClick={onLogoutClick}
                disabled={isPasswordSubmitting || isLogoutSubmitting}
              >
                {isLogoutSubmitting
                  ? t("common:navbar.actions.loggingOut")
                  : t("common:navbar.actions.logout")}
              </button>
            </div>
          )}
        </div>
      </div>

      {isPasswordModalOpen && (
        <div className="navbar__modalOverlay" role="dialog" aria-modal="true">
          <div className="navbar__modalContent">
            <h2>{t("common:navbar.password.title")}</h2>

            {passwordForm.errorMessage && (
              <p className="navbar__errorMessage" role="alert">
                {passwordForm.errorMessage}
              </p>
            )}

            {passwordForm.successMessage && (
              <p className="navbar__successMessage" aria-live="polite">
                {passwordForm.successMessage}
              </p>
            )}

            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleChangePassword();
              }}
            >
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={currentUser?.username || authState?.user?.username || ""}
                readOnly
                hidden
              />

              <input
                type="password"
                placeholder={t("common:navbar.password.placeholders.currentPassword")}
                value={passwordForm.currentPassword}
                onChange={(event) =>
                  updatePasswordField("currentPassword", event.target.value)
                }
                autoComplete="current-password"
                required
                disabled={isPasswordSubmitting}
              />

              <input
                type="password"
                placeholder={t("common:navbar.password.placeholders.newPassword")}
                value={passwordForm.newPassword}
                onChange={(event) =>
                  updatePasswordField("newPassword", event.target.value)
                }
                autoComplete="new-password"
                required
                disabled={isPasswordSubmitting}
              />

              <div className="navbar__modalActions">
                <button type="submit" disabled={isPasswordSubmitting}>
                  {isPasswordSubmitting
                    ? t("common:navbar.password.actions.updating")
                    : t("common:navbar.password.actions.changePassword")}
                </button>

                <button
                  type="button"
                  onClick={closePasswordModal}
                  disabled={isPasswordSubmitting}
                >
                  {t("common:actions.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
